import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import { getDependencyGraph } from '../../service/service';

// ── Types ──────────────────────────────────────────────
interface GraphNode {
  id: string;
  name: string;
}

interface GraphEdge {
  source: string;
  target: string;
}

interface GraphData {
  market: string;
  nodeCount: number;
  edgeCount: number;
  nodes: GraphNode[];
  edges: GraphEdge[];
}

interface LayoutNode extends GraphNode {
  x: number;
  y: number;
  level: number;
}

interface DependencyGraphProps {
  market?: string;
  businessProgram?: string;
  width?: number;
  height?: number;
}

// ── Layout Engine ──────────────────────────────────────

/**
 * Simple topological layered layout.
 * Assigns each node to a level (depth from sources),
 * then spaces them evenly within each level.
 */
function computeLayout(nodes: GraphNode[], edges: GraphEdge[]): LayoutNode[] {
  if (nodes.length === 0) return [];

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));
  const adj = new Map<string, string[]>();    // parent → children
  const inDeg = new Map<string, number>();     // in-degree
  const outDeg = new Map<string, number>();    // out-degree

  for (const n of nodes) {
    inDeg.set(n.id, 0);
    outDeg.set(n.id, 0);
    adj.set(n.id, []);
  }

  for (const e of edges) {
    if (!adj.has(e.source)) continue;
    adj.get(e.source)!.push(e.target);
    inDeg.set(e.target, (inDeg.get(e.target) || 0) + 1);
    outDeg.set(e.source, (outDeg.get(e.source) || 0) + 1);
  }

  // BFS to assign levels (longest path from any source)
  const level = new Map<string, number>();
  const queue: string[] = [];

  for (const n of nodes) {
    if ((inDeg.get(n.id) || 0) === 0) {
      level.set(n.id, 0);
      queue.push(n.id);
    }
  }

  // If all nodes have in-degree > 0 (pure cycle), start from first node
  if (queue.length === 0) {
    const firstId = nodes[0].id;
    level.set(firstId, 0);
    queue.push(firstId);
  }

  let iterations = 0;
  const maxIterations = nodes.length * nodes.length;
  while (queue.length > 0 && iterations < maxIterations) {
    iterations++;
    const curr = queue.shift()!;
    const currLevel = level.get(curr) || 0;
    for (const child of adj.get(curr) || []) {
      const newLevel = currLevel + 1;
      if (!level.has(child) || level.get(child)! < newLevel) {
        level.set(child, newLevel);
        queue.push(child);
      }
    }
  }

  // For any unvisited nodes (disconnected), assign level 0
  for (const n of nodes) {
    if (!level.has(n.id)) level.set(n.id, 0);
  }

  // Group nodes by level
  const levelGroups = new Map<number, string[]>();
  for (const n of nodes) {
    const l = level.get(n.id) || 0;
    if (!levelGroups.has(l)) levelGroups.set(l, []);
    levelGroups.get(l)!.push(n.id);
  }

  const maxLevel = Math.max(...levelGroups.keys(), 0);
  const nodeWidth = 180;
  const nodeHeight = 50;
  const levelGap = 140;
  const nodeGap = 30;
  const paddingX = 60;
  const paddingY = 60;

  const layoutNodes: LayoutNode[] = [];

  for (const [lvl, nodeIds] of levelGroups) {
    const totalWidth = nodeIds.length * nodeWidth + (nodeIds.length - 1) * nodeGap;
    const startX = paddingX;

    nodeIds.forEach((id, idx) => {
      const n = nodeMap.get(id)!;
      layoutNodes.push({
        ...n,
        x: startX + idx * (nodeWidth + nodeGap),
        y: paddingY + lvl * (nodeHeight + levelGap),
        level: lvl,
      });
    });
  }

  return layoutNodes;
}

// ── SVG Arrow Marker ───────────────────────────────────
const ArrowMarker: React.FC = () => (
  <defs>
    <marker
      id="arrowhead"
      markerWidth="10"
      markerHeight="7"
      refX="10"
      refY="3.5"
      orient="auto"
    >
      <polygon points="0 0, 10 3.5, 0 7" fill="#009BD8" />
    </marker>
    <marker
      id="arrowhead-hover"
      markerWidth="10"
      markerHeight="7"
      refX="10"
      refY="3.5"
      orient="auto"
    >
      <polygon points="0 0, 10 3.5, 0 7" fill="#003771" />
    </marker>
  </defs>
);

// ── Main Component ─────────────────────────────────────
export const DependencyGraph: React.FC<DependencyGraphProps> = ({
  market: initialMarket,
  businessProgram = 'IBP',
  width = 900,
  height = 600,
}) => {
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);
  const [selectedMarket, setSelectedMarket] = useState<string>(initialMarket || '');
  const svgRef = useRef<SVGSVGElement>(null);
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });

  // Available markets (could be fetched from API)
  const markets = ['PBNA', 'PFNA', 'FRANCE', 'BENELUX', 'IBERIA', 'EUROPE', 'MEXICO'];

  const fetchGraph = useCallback(async (mkt: string) => {
    if (!mkt) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getDependencyGraph(mkt, businessProgram);
      if (res?.data) {
        setGraphData(res.data);
      } else {
        setGraphData({ market: mkt, nodeCount: 0, edgeCount: 0, nodes: [], edges: [] });
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load dependency graph');
    } finally {
      setLoading(false);
    }
  }, [businessProgram]);

  useEffect(() => {
    if (selectedMarket) fetchGraph(selectedMarket);
  }, [selectedMarket, fetchGraph]);

  // Compute layout
  const layoutNodes = useMemo(() => {
    if (!graphData) return [];
    return computeLayout(graphData.nodes, graphData.edges);
  }, [graphData]);

  const nodeMap = useMemo(
    () => new Map(layoutNodes.map((n) => [n.id, n])),
    [layoutNodes]
  );

  // SVG dimensions based on layout
  const svgContentWidth = useMemo(() => {
    if (layoutNodes.length === 0) return width;
    const maxX = Math.max(...layoutNodes.map((n) => n.x)) + 220;
    return Math.max(maxX, width);
  }, [layoutNodes, width]);

  const svgContentHeight = useMemo(() => {
    if (layoutNodes.length === 0) return height;
    const maxY = Math.max(...layoutNodes.map((n) => n.y)) + 100;
    return Math.max(maxY, height);
  }, [layoutNodes, height]);

  // Zoom & pan handlers
  const handleZoomIn = () => setZoom((z) => Math.min(z * 1.2, 3));
  const handleZoomOut = () => setZoom((z) => Math.max(z / 1.2, 0.3));
  const handleReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    isDragging.current = true;
    dragStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current) return;
    setPan({
      x: e.clientX - dragStart.current.x,
      y: e.clientY - dragStart.current.y,
    });
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom((z) => Math.min(Math.max(z * delta, 0.3), 3));
  };

  // Build edge paths
  const edgePaths = useMemo(() => {
    const nodeWidth = 180;
    const nodeHeight = 50;
    const paths: { key: string; d: string; fromId: string; toId: string }[] = [];

    for (const edge of graphData?.edges || []) {
      const from = nodeMap.get(edge.source);
      const to = nodeMap.get(edge.target);
      if (!from || !to) continue;

      const x1 = from.x + nodeWidth;
      const y1 = from.y + nodeHeight / 2;
      const x2 = to.x;
      const y2 = to.y + nodeHeight / 2;

      // Straight line
      const d = `M ${x1} ${y1} L ${x2} ${y2}`;

      paths.push({
        key: `${edge.source}-${edge.target}`,
        d,
        fromId: edge.source,
        toId: edge.target,
      });
    }
    return paths;
  }, [graphData, nodeMap]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading dependency graph…</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const nodeWidth = 180;
  const nodeHeight = 50;

  return (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: 2, overflow: 'hidden', backgroundColor: '#FAFBFD' }}>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderBottom: '1px solid #E2E8F0', backgroundColor: '#F1F5F9' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Market</InputLabel>
          <Select
            value={selectedMarket}
            label="Market"
            onChange={(e) => setSelectedMarket(e.target.value)}
          >
            {markets.map((m) => (
              <MenuItem key={m} value={m}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Chip
          label={`${graphData?.nodeCount || 0} programs`}
          size="small"
          sx={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}
        />
        <Chip
          label={`${graphData?.edgeCount || 0} dependencies`}
          size="small"
          sx={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
        />

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Zoom In">
          <IconButton size="small" onClick={handleZoomIn}><ZoomInIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton size="small" onClick={handleZoomOut}><ZoomOutIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Reset View">
          <IconButton size="small" onClick={handleReset}><CenterFocusStrongIcon /></IconButton>
        </Tooltip>

        <Typography variant="caption" color="text.secondary">
          {Math.round(zoom * 100)}%
        </Typography>
      </Box>

      {/* Graph Canvas */}
      <Box
        sx={{
          width: '100%',
          height,
          overflow: 'hidden',
          cursor: isDragging.current ? 'grabbing' : 'grab',
          position: 'relative',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
      >
        {layoutNodes.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">
              No dependencies found for this market. Use the bulk upload to add dependencies.
            </Typography>
          </Box>
        ) : (
          <svg
            ref={svgRef}
            width={svgContentWidth}
            height={svgContentHeight}
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
            }}
          >
            <ArrowMarker />

            {/* Edges */}
            {edgePaths.map((ep) => {
              const isHighlighted =
                hoveredNode === ep.fromId || hoveredNode === ep.toId || hoveredEdge === ep.key;
              return (
                <path
                  key={ep.key}
                  d={ep.d}
                  fill="none"
                  stroke={isHighlighted ? '#003771' : '#94A3B8'}
                  strokeWidth={isHighlighted ? 2.5 : 1.5}
                  strokeDasharray={isHighlighted ? 'none' : '6,3'}
                  markerEnd={isHighlighted ? 'url(#arrowhead-hover)' : 'url(#arrowhead)'}
                  style={{ transition: 'all 0.2s ease' }}
                  onMouseEnter={() => setHoveredEdge(ep.key)}
                  onMouseLeave={() => setHoveredEdge(null)}
                />
              );
            })}

            {/* Nodes */}
            {layoutNodes.map((node) => {
              const isHovered = hoveredNode === node.id;
              const isConnected =
                hoveredNode &&
                graphData?.edges.some(
                  (e) =>
                    (e.source === hoveredNode && e.target === node.id) ||
                    (e.target === hoveredNode && e.source === node.id)
                );

              return (
                <g
                  key={node.id}
                  onMouseEnter={() => setHoveredNode(node.id)}
                  onMouseLeave={() => setHoveredNode(null)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* Node background */}
                  <rect
                    x={node.x}
                    y={node.y}
                    width={nodeWidth}
                    height={nodeHeight}
                    rx={8}
                    ry={8}
                    fill={isHovered ? '#009BD8' : isConnected ? '#E0F2FE' : '#FFFFFF'}
                    stroke={isHovered ? '#003771' : isConnected ? '#009BD8' : '#CBD5E1'}
                    strokeWidth={isHovered || isConnected ? 2 : 1}
                    style={{ transition: 'all 0.2s ease' }}
                  />

                  {/* Node label */}
                  <text
                    x={node.x + nodeWidth / 2}
                    y={node.y + nodeHeight / 2 - 4}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={isHovered ? '#FFFFFF' : '#1E293B'}
                    fontSize={12}
                    fontWeight={600}
                    fontFamily="Inter, sans-serif"
                  >
                    {node.name.length > 22 ? node.name.slice(0, 20) + '…' : node.name}
                  </text>


                </g>
              );
            })}
          </svg>
        )}
      </Box>

      {/* Legend */}
      <Box sx={{ p: 1.5, borderTop: '1px solid #E2E8F0', display: 'flex', gap: 3, alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 24, height: 2, backgroundColor: '#94A3B8', borderStyle: 'dashed' }} />
          <Typography variant="caption" color="text.secondary">Dependency</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#009BD8' }} />
          <Typography variant="caption" color="text.secondary">Hovered</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Drag to pan • Scroll to zoom • Hover nodes to highlight connections
        </Typography>
      </Box>
    </Box>
  );
};

export default DependencyGraph;
